(function () {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const desktopQuery = window.matchMedia('(min-width: 750px)');

  class FeaturedProductsShowcase extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.carouselBindings = [];
      this.initWishlist();
      this.initCarousels();
      this.initTabs();
      this.initMotion();
      this.onShopifyBlockSelect = this.onShopifyBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onShopifyBlockSelect);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.carouselBindings.forEach((binding) => this.unbindCarousel(binding));
      this.carouselBindings = [];
      document.removeEventListener('shopify:block:select', this.onShopifyBlockSelect);
    }

    initWishlist() {
      this.querySelectorAll('.fps-card__wishlist').forEach((button) => {
        if (button.dataset.fpsWishlistBound) return;
        button.dataset.fpsWishlistBound = 'true';

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const isActive = button.classList.toggle('is-active');
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      });
    }

    initMotion() {
      if (!this.classList.contains('fps--motion')) return;

      if (prefersReducedMotion.matches || (typeof Shopify !== 'undefined' && Shopify.designMode)) {
        this.classList.add('fps-motion-ready');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.classList.add('fps-motion-ready');
            this.observer.disconnect();
            this.observer = null;
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
      );

      this.observer.observe(this);
    }

    initTabs() {
      if (this.dataset.collectionTabs !== 'true') return;

      this.tabs = Array.from(this.querySelectorAll('[data-fps-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-fps-tab-panel]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.fpsTab));
      });
    }

    onShopifyBlockSelect(event) {
      if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
      const blockId = event.detail.blockId;
      if (!blockId) return;

      if (this.querySelector(`[data-fps-tab="${blockId}"]`)) {
        this.activateTab(blockId);
      }
    }

    activateTab(tabId) {
      if (!tabId || !this.tabs) return;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.fpsTab === tabId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          tab.scrollIntoView({ behavior: prefersReducedMotion.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        }
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.fpsTabPanel === tabId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      const activePanel = this.panels.find((panel) => panel.dataset.fpsTabPanel === tabId);
      if (activePanel) {
        const slider = activePanel.querySelector('.fps__grid--mobile-slider, .fps__grid--desktop-slider');
        if (slider) slider.scrollLeft = 0;
        this.updateCarouselArrows(activePanel);
        this.onCarouselScroll(activePanel);
      }
    }

    initCarousels() {
      this.querySelectorAll('[data-fps-carousel]').forEach((carousel) => {
        this.bindCarousel(carousel);
      });
    }

    bindCarousel(carousel) {
      if (carousel.dataset.fpsBound === 'true') return;
      carousel.dataset.fpsBound = 'true';

      const binding = {
        carousel,
        slider: carousel.querySelector('.fps__grid--mobile-slider, .fps__grid--desktop-slider'),
        mobileNav: carousel.querySelector('[data-fps-mobile-nav]'),
        prev: carousel.querySelector('.fps__arrow--prev'),
        next: carousel.querySelector('.fps__arrow--next'),
        onScroll: null,
        onWheel: null,
        onResize: null,
      };

      if (!binding.slider) return;

      binding.slides = () => Array.from(binding.slider.querySelectorAll('.fps__item--slide'));
      binding.segments = binding.mobileNav
        ? Array.from(binding.mobileNav.querySelectorAll('.fps__mobile-nav-segment'))
        : [];

      binding.onScroll = () => this.onCarouselScroll(carousel);
      binding.slider.addEventListener('scroll', binding.onScroll, { passive: true });

      binding.onWheel = (event) => {
        if (!mobileQuery.matches || !binding.slider.classList.contains('fps__grid--mobile-slider')) return;
        const deltaY = event.deltaY;
        if (!deltaY || Math.abs(deltaY) <= Math.abs(event.deltaX)) return;
        event.preventDefault();
        window.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
      };
      binding.slider.addEventListener('wheel', binding.onWheel, { passive: false });

      binding.segments.forEach((segment) => {
        segment.addEventListener('click', () => {
          const index = parseInt(segment.dataset.slideIndex, 10);
          this.goToSlide(binding, index);
        });
      });

      if (binding.prev) {
        binding.prev.addEventListener('click', () => this.scrollCarouselBy(binding, -1));
      }

      if (binding.next) {
        binding.next.addEventListener('click', () => this.scrollCarouselBy(binding, 1));
      }

      binding.onResize = () => this.updateCarouselArrows(carousel);
      window.addEventListener('resize', binding.onResize);

      this.carouselBindings.push(binding);
      this.updateCarouselArrows(carousel);
      this.onCarouselScroll(carousel);
    }

    unbindCarousel(binding) {
      if (!binding) return;
      if (binding.slider && binding.onScroll) {
        binding.slider.removeEventListener('scroll', binding.onScroll);
      }
      if (binding.slider && binding.onWheel) {
        binding.slider.removeEventListener('wheel', binding.onWheel);
      }
      if (binding.onResize) {
        window.removeEventListener('resize', binding.onResize);
      }
    }

    getVisibleCarousel(carouselEl) {
      const panel = carouselEl.closest('[data-fps-tab-panel]');
      if (panel && panel.hidden) return null;
      return carouselEl;
    }

    onCarouselScroll(carouselEl) {
      const carousel = carouselEl.matches('[data-fps-carousel]')
        ? carouselEl
        : carouselEl.querySelector('[data-fps-carousel]') || carouselEl.closest('[data-fps-carousel]');
      if (!carousel || carouselEl.hidden) return;

      const binding = this.carouselBindings.find((item) => item.carousel === carousel);
      if (!binding || !binding.slider) return;

      if (binding._ticking) return;
      binding._ticking = true;

      window.requestAnimationFrame(() => {
        this.setActiveSegment(binding, this.getCurrentSlideIndex(binding));
        this.updateCarouselArrows(binding.carousel);
        binding._ticking = false;
      });
    }

    getCurrentSlideIndex(binding) {
      if (!binding.slider || !binding.slides().length) return 0;

      const sliderCenter = binding.slider.scrollLeft + binding.slider.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      binding.slides().forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(sliderCenter - slideCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    setActiveSegment(binding, index) {
      if (!binding.segments.length) return;

      binding.segments.forEach((segment, segmentIndex) => {
        const isActive = segmentIndex === index;
        segment.classList.toggle('is-active', isActive);
        segment.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    goToSlide(binding, index) {
      const slide = binding.slides()[index];
      if (!slide || !binding.slider) return;

      const targetLeft = slide.offsetLeft - (binding.slider.clientWidth - slide.offsetWidth) / 2;
      const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';

      binding.slider.scrollTo({ left: targetLeft, behavior });
      this.setActiveSegment(binding, index);
    }

    scrollCarouselBy(binding, direction) {
      const slide = binding.slides()[0];
      if (!slide || !binding.slider) return;

      const gap = parseFloat(getComputedStyle(binding.slider).columnGap || getComputedStyle(binding.slider).gap || '0');
      const scrollAmount = slide.offsetWidth + gap;
      const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';

      binding.slider.scrollBy({ left: scrollAmount * direction, behavior });
    }

    updateCarouselArrows(carouselEl) {
      const carousel = carouselEl.matches('[data-fps-carousel]')
        ? carouselEl
        : carouselEl.querySelector('[data-fps-carousel]') || carouselEl.closest('[data-fps-carousel]');
      if (!carousel) return;

      const binding = this.carouselBindings.find((item) => item.carousel === carousel);
      if (!binding || !binding.slider || !binding.prev || !binding.next) return;

      const panel = carousel.closest('[data-fps-tab-panel]');
      if (panel && panel.hidden) return;

      if (!desktopQuery.matches) {
        binding.prev.disabled = true;
        binding.next.disabled = true;
        return;
      }

      const maxScroll = binding.slider.scrollWidth - binding.slider.clientWidth;
      const scrollLeft = binding.slider.scrollLeft;
      const threshold = 4;

      binding.prev.disabled = scrollLeft <= threshold;
      binding.next.disabled = scrollLeft >= maxScroll - threshold;
    }
  }

  if (!customElements.get('featured-products-showcase')) {
    customElements.define('featured-products-showcase', FeaturedProductsShowcase);
  }
})();
